import type React from 'react';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Flex, Heading, IconChevronDown, IconChevronUp, TextButton, VisuallyHidden } from '@zillow/constellation';
import * as S from './styles';

import MarketPreview from './MarketPreview';
import FilterTray from 'app/shared/components/FilterTray';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';

import type { State } from 'app/shared/types/State';
import type { Market } from 'app/shared/types/Market';

interface MarketPreviewsProps {
  markets: Array<Market>;
  heading: string;
  subject: string;
  useFilters?: boolean;
  isSmallScreen: boolean;
  isMobile: boolean;
  emitAnalyticsEvent?: () => void;
}

const MarketPreviews: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketPreviewsProps>>> = (
  props,
) => {
  const { heading, subject, useFilters = false, isSmallScreen, isMobile, emitAnalyticsEvent } = props;
  const dispatch = useDispatch();
  const filter = useSelector((state: State) => state.filter);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const expand = useCallback(() => setIsExpanded(!isExpanded), [isExpanded, setIsExpanded]);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);

  const markets = props.markets.filter((market) => market.medianRent !== 0); // dont include when theres no data
  const filterDisplay = (
    <FilterTray
      bedrooms={bedrooms}
      setBedrooms={setBedrooms}
      propertyTypes={propertyTypes}
      setPropertyTypes={setPropertyTypes}
      loading={loading}
      setLoading={setLoading}
      isMobile={isMobile}
      style={isSmallScreen ? {} : { marginRight: 60 }}
    />
  );
  return (
    <Card
      cardType="secondary"
      padding="md"
      paddingBottom="md"
      paddingX={isSmallScreen ? undefined : 'md'}
      marginTop="md"
    >
      <Heading level="4" as="h2" fontFamily="serif">
        {heading}
      </Heading>
      {useFilters &&
        (isSmallScreen ? (
          filterDisplay
        ) : (
          <Flex display="flex" flexDirection="row" justifyContent="flex-end" flexWrap="wrap">
            {filterDisplay}
          </Flex>
        ))}

      <S.MarketPreviewContainer
        display="grid"
        gap={isSmallScreen ? 'xs' : 'lg'}
        marginBottom="lg"
        // the filters act as a spacer, so need to add margin when the filters are not present
        marginTop={useFilters ? undefined : 'md'}
        loading={loading}
        isSmallScreen={isSmallScreen}
      >
        {markets.map((market, i) => {
          // only show first 4 top markets on mobile
          if (isSmallScreen && i > 3 && !isExpanded) {
            return (
              <VisuallyHidden key={`${market.name}-hidden`}>
                <MarketPreview {...market} isSmallScreen={isSmallScreen} emitAnalyticsEvent={emitAnalyticsEvent} />
              </VisuallyHidden>
            );
          }
          return (
            <MarketPreview
              {...market}
              isSmallScreen={isSmallScreen}
              key={market.name}
              emitAnalyticsEvent={emitAnalyticsEvent}
            />
          );
        })}
      </S.MarketPreviewContainer>
      {markets.length > 4 && isSmallScreen && (
        <TextButton onClick={expand}>
          {isExpanded ? (
            <>
              See less {subject}
              <IconChevronUp />
            </>
          ) : (
            <>
              See more {subject}
              <IconChevronDown />
            </>
          )}
        </TextButton>
      )}
    </Card>
  );
};

export default MarketPreviews;
